/**
 * Dashboard API Service
 * 
 * Feature: 036-unified-dashboard
 * Description: Frontend API client for dashboard operations
 * Constitution: v1.11.0
 */

import { apiClient } from './apiClient';
import { AxiosResponse } from 'axios';

const API_BASE = '/api/dashboards';

export interface DashboardConfig {
  id: string;
  userId: string;
  dashboardType: string;
  layoutConfig: Record<string, unknown>;
  widgetConfig: Record<string, unknown>;
  capacityTypeFilter?: string;
  refreshInterval: number;
  createdAt: string;
  updatedAt: string;
}

export interface Widget {
  id: string;
  widgetType: string;
  displayName: string;
  isActive: boolean;
  position: { x: number; y: number; w: number; h: number };
  settings: Record<string, unknown>;
}

export interface CapacitySummary {
  capacityType: string;
  count: number;
  healthScore: number;
  status: string;
  lastUpdated: string;
  // Extended properties for dashboard display
  totalCount?: number;
  healthyCount?: number;
  warningCount?: number;
  criticalCount?: number;
  avgCpu?: number;
  avgMemory?: number;
  trend?: 'improving' | 'stable' | 'degrading';
}

export interface Activity {
  id: string;
  activityType: string;
  description: string;
  userId: string;
  timestamp: string;
  metadata: Record<string, unknown>;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Handle axios response and convert to ApiResponse format
 * Note: Backend already returns { success, data } format, so we unwrap it
 */
async function handleAxiosResponse<T>(promise: Promise<AxiosResponse<T | ApiResponse<T>>>): Promise<ApiResponse<T>> {
  try {
    const response = await promise;
    const responseData = response.data as T | ApiResponse<T>;
    
    // Check if the backend already wrapped the response in { success, data }
    if (responseData && typeof responseData === 'object' && 'success' in responseData && 'data' in responseData) {
      // Backend already wrapped it - unwrap and return
      const wrapped = responseData as ApiResponse<T>;
      return {
        success: wrapped.success,
        data: wrapped.data,
        error: wrapped.error,
        message: wrapped.message
      };
    }
    
    // Backend returned raw data - wrap it
    return {
      success: true,
      data: responseData as T
    };
  } catch (error: unknown) {
    const axiosError = error as { response?: { data?: { error?: string; message?: string }; status?: number; statusText?: string } };
    const errorData = axiosError.response?.data || {};
    return {
      success: false,
      error: errorData.error || errorData.message || `HTTP ${axiosError.response?.status}: ${axiosError.response?.statusText}`
    };
  }
}

/**
 * Dashboard API client
 */
export const dashboardApi = {
  /**
   * Get dashboard configuration for current user
   */
  async getConfig(dashboardType: string = 'unified'): Promise<ApiResponse<DashboardConfig>> {
    return handleAxiosResponse(apiClient.get<DashboardConfig>(`${API_BASE}/config?type=${encodeURIComponent(dashboardType)}`));
  },

  /**
   * Save dashboard configuration
   */
  async saveConfig(config: Partial<DashboardConfig>): Promise<ApiResponse<DashboardConfig>> {
    return handleAxiosResponse(apiClient.post<DashboardConfig>(`${API_BASE}/config`, config));
  },

  /**
   * Update dashboard configuration
   */
  async updateConfig(id: string, updates: Partial<DashboardConfig>): Promise<ApiResponse<DashboardConfig>> {
    return handleAxiosResponse(apiClient.patch<DashboardConfig>(`${API_BASE}/config/${id}`, updates));
  },

  /**
   * Get user's widget preferences
   */
  async getWidgets(): Promise<ApiResponse<Widget[]>> {
    return handleAxiosResponse(apiClient.get<Widget[]>(`${API_BASE}/widgets`));
  },

  /**
   * Update widget positions
   */
  async updateWidgetPositions(
    positions: Array<{ widgetId: string; position: { x: number; y: number; w: number; h: number } }>
  ): Promise<ApiResponse<void>> {
    return handleAxiosResponse(apiClient.put<void>(`${API_BASE}/widgets/positions`, { positions }));
  },

  /**
   * Toggle widget visibility
   */
  async toggleWidget(widgetId: string, isActive: boolean): Promise<ApiResponse<Widget>> {
    return handleAxiosResponse(apiClient.patch<Widget>(`${API_BASE}/widgets/${widgetId}/toggle`, { isActive }));
  },

  /**
   * Get capacity summaries
   * Note: Backend returns snake_case (T039 schema alignment)
   */
  async getCapacitySummaries(capacityType?: string): Promise<ApiResponse<CapacitySummary[]>> {
    const url = capacityType 
      ? `${API_BASE}/capacity-summaries?type=${encodeURIComponent(capacityType)}`
      : `${API_BASE}/capacity-summaries`;
    
    const raw = await handleAxiosResponse<Record<string, unknown>[]>(apiClient.get(url));
    if (!raw.success || !raw.data) return { success: raw.success, error: raw.error } as ApiResponse<CapacitySummary[]>;
    
    // Map snake_case backend fields → camelCase frontend interface
    const mapped: CapacitySummary[] = raw.data.map((s) => ({
      capacityType: (s.capacity_type || s.capacityType || '') as string,
      count: (s.total_count || s.count || 0) as number,
      healthScore: (s.health_score || s.healthScore || 0) as number,
      status: (s.status || 'Unknown') as string,
      lastUpdated: (s.last_updated || s.lastUpdated || new Date().toISOString()) as string,
      totalCount: (s.total_count || s.totalCount || 0) as number,
      healthyCount: (s.healthy_count || s.healthyCount || 0) as number,
      warningCount: (s.warning_count || s.warningCount || 0) as number,
      criticalCount: (s.critical_count || s.criticalCount || 0) as number,
      avgCpu: (s.avg_cpu || s.avgCpu || 0) as number,
      avgMemory: (s.avg_memory || s.avgMemory || 0) as number,
      trend: (s.trend as CapacitySummary['trend']) || 'stable',
    }));
    return { success: true, data: mapped };
  },

  /**
   * Get recent activities
   * Note: Activities have their own endpoint at /api/activities
   */
  async getRecentActivities(
    limit: number = 20,
    capacityType?: string
  ): Promise<ApiResponse<Activity[]>> {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (capacityType) {
      params.append('capacityType', capacityType);
    }

    return handleAxiosResponse(apiClient.get<Activity[]>(`/api/activities?${params}`));
  },

  /**
   * Log dashboard activity
   */
  async logActivity(
    activityType: string,
    description: string,
    metadata?: Record<string, unknown>
  ): Promise<ApiResponse<Activity>> {
    return handleAxiosResponse(apiClient.post<Activity>(`${API_BASE}/activities`, { activityType, description, metadata }));
  },

  /**
   * Export dashboard data
   */
  async exportDashboard(format: 'json' | 'csv' | 'pdf' = 'json'): Promise<Blob> {
    const response = await apiClient.get<Blob>(`${API_BASE}/export?format=${format}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  /**
   * Refresh dashboard data
   */
  async refreshData(): Promise<ApiResponse<{ refreshedAt: string }>> {
    return handleAxiosResponse(apiClient.post<{ refreshedAt: string }>(`${API_BASE}/refresh`));
  }
};

export default dashboardApi;